import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { useDeleteSearchHistory } from 'modules/comparisonPond/api/mutation'
import { useGetSearchHistory } from 'modules/comparisonPond/api/queries'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Button,
  Paper,
  useTheme,
  IconButton,
} from '@mui/material'

import AddCompareDialog from '../ComparisonAddCompareDialog'

const CycleMultiSelect: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation('pondComparison')
  const { data, isLoading } = useGetSearchHistory()
  const deleteSearchHistory = useDeleteSearchHistory()
  const [dialogOpen, setDialogOpen] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const idParam = searchParams.get('cycleIds')

  const urlCycleIds = useMemo(() => (idParam ? idParam.split(',').filter(Boolean) : []), [idParam])

  const selectedOptions = useMemo(
    () => (data ?? []).filter((o) => urlCycleIds.includes(String(o.cycle_id))),
    [data, urlCycleIds]
  )

  const selectedSpeciesId = useMemo(() => selectedOptions[0]?.species?.id, [selectedOptions])

  const PaperWithFooter = (props: any) => (
    <Paper {...props}>
      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2">
          {t('lastSelectedCycles', { defaultValue: 'รอบการเลือกล่าสุด' })}
        </Typography>
      </Box>
      {props.children}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 1,
          borderTop: `2px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setDialogOpen(true)
          }}
          startIcon={<AddIcon />}
          size="small"
          fullWidth
          variant="outlined"
        >
          {t('addCompare')}
        </Button>
      </Box>
    </Paper>
  )

  return (
    <Box sx={{ mb: 3 }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={data ?? []}
        renderOption={(props: any, option) => {
          const label = t('optionLabel', {
            farm: option.farm_name,
            pond: option.pond_name,
            start: formatThaiShortDateTwoDigitYear(option.start_date),
            endText: ` - ${
              option.end_date ? formatThaiShortDateTwoDigitYear(option.end_date) : t('present')
            }`,
          })
          return (
            <li
              {...props}
              key={option.cycle_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 8,
              }}
            >
              <span style={{ marginRight: 8 }}>{label}</span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  deleteSearchHistory.mutate(option.id || '')
                  const remaining = urlCycleIds.filter((id) => id !== String(option.cycle_id))
                  const params = new URLSearchParams(searchParams)
                  if (remaining.length) params.set('cycleIds', remaining.join(','))
                  else params.delete('cycleIds')
                  setSearchParams(params)
                }}
                aria-label="delete-history"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </li>
          )
        }}
        getOptionLabel={(o) => {
          const start = formatThaiShortDateTwoDigitYear(o.start_date)
          const end = o.end_date ? formatThaiShortDateTwoDigitYear(o.end_date) : t('present')
          return t('optionLabel', {
            farm: o.farm_name,
            pond: o.pond_name,
            start,
            endText: ` - ${end}`,
          })
        }}
        isOptionEqualToValue={(a, b) => a.cycle_id === b.cycle_id}
        getOptionDisabled={(option) =>
          selectedSpeciesId !== undefined && option.species?.id !== selectedSpeciesId
        }
        value={selectedOptions}
        onChange={(_event, value) => {
          const ids = value.map((v) => String(v.cycle_id))
          const params = new URLSearchParams(searchParams)
          if (ids.length) params.set('cycleIds', ids.join(','))
          else params.delete('cycleIds')
          setSearchParams(params)
        }}
        loading={isLoading}
        loadingText={t('common.loading', { defaultValue: 'กำลังโหลด...' })}
        PaperComponent={PaperWithFooter}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('selectComparison', { defaultValue: 'เลือกการเปรียบเทียบ' })}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={
          isLoading
            ? t('common.loading', { defaultValue: 'กำลังโหลด...' })
            : t('common.noOptions', { defaultValue: 'ไม่พบตัวเลือก' })
        }
      />

      <AddCompareDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Box>
  )
}

export default CycleMultiSelect
